import app from '../firebase';
import FirebaseStorageAdapter from './storage/FirebaseStorageAdapter';

/**
 * Storage Service
 * Singleton service for managing file uploads using Firebase Storage
 */
class StorageService {
  constructor() {
    this.adapter = new FirebaseStorageAdapter(app);
    console.log('Storage Service: Initialized with Firebase Storage');
  }

  /**
   * Check if the storage service is properly configured
   * @returns {boolean}
   */
  isConfigured() {
    return this.adapter !== null;
  }

  /**
   * Upload a product image
   * @param {File} file - The image file to upload
   * @returns {Promise<string>} - The public URL of the uploaded image
   */
  async uploadProductImage(file) {
    if (!this.adapter) {
      throw new Error('Storage service not configured');
    }

    try {
      const url = await this.adapter.upload(file, 'products');
      return url;
    } catch (error) {
      console.error('Error uploading product image:', error);
      throw error;
    }
  }

  /**
   * Upload a QR code image
   * @param {File} file - The QR code image file to upload
   * @returns {Promise<string>} - The public URL of the uploaded QR code
   */
  async uploadQRCode(file) {
    if (!this.adapter) {
      throw new Error('Storage service not configured');
    }

    try {
      const url = await this.adapter.upload(file, 'qr-codes');
      return url;
    } catch (error) {
      console.error('Error uploading QR code:', error);
      throw error;
    }
  }

  /**
   * Delete an image from storage
   * @param {string} url - The URL of the image to delete
   * @returns {Promise<void>}
   */
  async deleteImage(url) {
    if (!this.adapter) {
      throw new Error('Storage service not configured');
    }

    try {
      await this.adapter.delete(url);
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }

  /**
   * Get the public URL for a file path
   * @param {string} path - The file path in storage
   * @returns {Promise<string>} - The public URL
   */
  async getUrl(path) {
    if (!this.adapter) {
      throw new Error('Storage service not configured');
    }

    try {
      const url = await this.adapter.getUrl(path);
      return url;
    } catch (error) {
      console.error('Error getting URL:', error);
      throw error;
    }
  }
}

// Export singleton instance
const storageService = new StorageService();
export default storageService;
